import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { FormEvent, useEffect, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  useCareGuideAsset,
  useSaveTenantBrandSettings,
  useTenantBrandSettings,
  useUploadCareGuideAsset,
  type TenantBrandSettings,
} from "../queries";
import { rootRoute } from "./__root";

const textFields: Array<keyof TenantBrandSettings> = [
  "brand_name",
  "legal_entity",
  "business_address",
  "gm_name",
  "gm_email",
  "gm_phone",
  "callback_phone",
  "owner_name",
  "owner_email",
  "dashboard_base_url",
  "feedback_base_url",
  "booking_link",
  "public_marketing_base_url",
  "public_reviews_url",
  "unsubscribe_email",
  "cockpit_base_url",
  "smtp_from_name",
  "smtp_from_email",
];

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/brand",
  component: BrandScreen,
});

function BrandScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner"]}>
      <BrandContent />
    </ProtectedRoute>
  );
}

function BrandContent() {
  const brand = useTenantBrandSettings();
  const careGuide = useCareGuideAsset();
  const uploadCareGuide = useUploadCareGuideAsset();
  const save = useSaveTenantBrandSettings();
  const [form, setForm] = useState<Partial<TenantBrandSettings>>({});
  const [originsText, setOriginsText] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");

  useEffect(() => {
    if (!brand.data) return;
    setForm(brand.data);
    setOriginsText((brand.data.allowed_origins || []).join("\n"));
  }, [brand.data]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload: Partial<TenantBrandSettings> = {
      vonigo_franchise_id: Number(form.vonigo_franchise_id || 1),
      allowed_origins: originsText.split(/\n+/).map((value) => value.trim()).filter(Boolean),
    };
    const editable = payload as Record<string, unknown>;
    for (const field of textFields) {
      editable[field] = String(form[field] || "");
    }
    await save.mutateAsync(payload);
  }

  async function uploadFile(file: File | null) {
    setUploadMessage("");
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setUploadMessage("Please upload a PDF file.");
      return;
    }
    await uploadCareGuide.mutateAsync(file);
    setUploadMessage("Care guide updated.");
  }

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Settings</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Brand</h1>
        <p className="mt-1 text-sm text-slate-600">Franchise identity, sender profile, phones, URLs, and allowed origins.</p>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader><CardTitle>Tenant config</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Brand name"><Input value={form.brand_name || ""} onChange={(event) => setForm({ ...form, brand_name: event.target.value })} /></Field>
                <Field label="Legal entity"><Input value={form.legal_entity || ""} onChange={(event) => setForm({ ...form, legal_entity: event.target.value })} /></Field>
                <Field label="GM name"><Input value={form.gm_name || ""} onChange={(event) => setForm({ ...form, gm_name: event.target.value })} /></Field>
                <Field label="GM email"><Input value={form.gm_email || ""} onChange={(event) => setForm({ ...form, gm_email: event.target.value })} /></Field>
                <Field label="GM phone"><Input value={form.gm_phone || ""} onChange={(event) => setForm({ ...form, gm_phone: event.target.value })} /></Field>
                <Field label="Callback phone"><Input value={form.callback_phone || ""} onChange={(event) => setForm({ ...form, callback_phone: event.target.value })} /></Field>
                <Field label="Owner name"><Input value={form.owner_name || ""} onChange={(event) => setForm({ ...form, owner_name: event.target.value })} /></Field>
                <Field label="Owner email"><Input value={form.owner_email || ""} onChange={(event) => setForm({ ...form, owner_email: event.target.value })} /></Field>
              </div>
              <Field label="Business address"><Input value={form.business_address || ""} onChange={(event) => setForm({ ...form, business_address: event.target.value })} /></Field>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Dashboard base URL"><Input value={form.dashboard_base_url || ""} onChange={(event) => setForm({ ...form, dashboard_base_url: event.target.value })} /></Field>
                <Field label="Cockpit base URL"><Input value={form.cockpit_base_url || ""} onChange={(event) => setForm({ ...form, cockpit_base_url: event.target.value })} /></Field>
                <Field label="Feedback base URL"><Input value={form.feedback_base_url || ""} onChange={(event) => setForm({ ...form, feedback_base_url: event.target.value })} /></Field>
                <Field label="Customer booking link"><Input value={form.booking_link || ""} onChange={(event) => setForm({ ...form, booking_link: event.target.value })} /></Field>
                <Field label="Public marketing origin"><Input value={form.public_marketing_base_url || ""} onChange={(event) => setForm({ ...form, public_marketing_base_url: event.target.value })} /></Field>
                <Field label="Public reviews URL"><Input value={form.public_reviews_url || ""} onChange={(event) => setForm({ ...form, public_reviews_url: event.target.value })} /></Field>
                <Field label="Unsubscribe email"><Input value={form.unsubscribe_email || ""} onChange={(event) => setForm({ ...form, unsubscribe_email: event.target.value })} /></Field>
                <Field label="Vonigo franchise ID"><Input type="number" value={form.vonigo_franchise_id || 1} onChange={(event) => setForm({ ...form, vonigo_franchise_id: Number(event.target.value) })} /></Field>
                <Field label="SMTP from name"><Input value={form.smtp_from_name || ""} onChange={(event) => setForm({ ...form, smtp_from_name: event.target.value })} /></Field>
                <Field label="SMTP from email"><Input value={form.smtp_from_email || ""} onChange={(event) => setForm({ ...form, smtp_from_email: event.target.value })} /></Field>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Allowed origins</span>
                <textarea
                  className="mt-1 min-h-28 w-full rounded-md border border-slate-200 p-3 text-sm"
                  value={originsText}
                  onChange={(event) => setOriginsText(event.target.value)}
                />
              </label>
              <Button disabled={save.isPending || !brand.data} type="submit">Save brand settings</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="rounded-md border border-slate-200 p-4">
              <div className="text-base font-semibold text-slate-950">{form.brand_name || "Brand"}</div>
              <div className="mt-2 text-sm text-slate-600">{form.legal_entity || "Legal entity"}</div>
              <div className="mt-4 text-sm text-slate-700">
                {form.gm_name || "GM"}<br />
                General Manager<br />
                {form.gm_phone || "Phone"}
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
                {form.business_address || "Business address"}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Care guide PDF</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Current file</div>
                <div className="mt-1 font-medium text-slate-950">
                  {careGuide.data?.filename || "Default bundled care guide"}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {careGuide.data?.updated_at
                    ? `Updated ${new Date(careGuide.data.updated_at).toLocaleString()}`
                    : "Using the bundled tenant fallback until a custom PDF is uploaded."}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {careGuide.data?.download_url && (
                  <a
                    className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 hover:bg-slate-50"
                    href={careGuide.data.download_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View PDF
                  </a>
                )}
                <label className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-orange-600 px-3 text-sm font-medium text-white hover:bg-orange-700">
                  Replace PDF
                  <input
                    className="hidden"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) => uploadFile(event.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500">
                The automated invoice email attaches this tenant-specific PDF. Uploading here only affects the current tenant.
              </p>
              {(uploadMessage || uploadCareGuide.isError) && (
                <p className={uploadCareGuide.isError ? "text-xs text-red-600" : "text-xs text-emerald-700"}>
                  {uploadCareGuide.isError ? "Upload failed. Please try again." : uploadMessage}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span><div className="mt-1">{children}</div></label>;
}
